import React, { useState } from 'react';
import { X, ShieldCheck, Phone, CreditCard } from 'lucide-react';
import { abdmApi } from '@/shared/api/client';

// ── Types ─────────────────────────────────────────────────────────

export interface AbhaProfile {
  abhaNumber:  string;
  abhaAddress: string;
  name:        string;
  linked:      boolean;
}

interface Props {
  patientId?:     string;
  defaultMethod?: 'aadhaar' | 'mobile';
  onVerified:     (profile: AbhaProfile) => void;
  onClose:        () => void;
}

// ── State machine ─────────────────────────────────────────────────
type ModalState = 'METHOD' | 'OTP_SENT' | 'PROFILE';
type Method     = 'aadhaar' | 'mobile';

// ── Styles ────────────────────────────────────────────────────────
const inputCls  = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/50';
const btnPrimary = 'w-full py-2 rounded-xl bg-navy text-white text-sm font-medium hover:bg-navy/90 transition disabled:opacity-50';
const btnSecondary = 'w-full py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition';

// ── Component ─────────────────────────────────────────────────────

export default function AbhaVerificationModal({ patientId, defaultMethod, onVerified, onClose }: Props) {
  const [stage, setStage]       = useState<ModalState>('METHOD');
  const [method, setMethod]     = useState<Method>(defaultMethod ?? 'aadhaar');
  const [input, setInput]       = useState('');
  const [otp, setOtp]           = useState('');
  const [txnId, setTxnId]       = useState('');
  const [mobileHint, setMobileHint] = useState('');
  const [profile, setProfile]   = useState<AbhaProfile | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  function clearError() { setError(''); }

  // ── Step 1: send OTP ────────────────────────────────────────────

  async function handleSendOtp() {
    clearError();
    setLoading(true);
    try {
      if (method === 'aadhaar') {
        if (!/^\d{12}$/.test(input)) {
          setError('Aadhaar number must be exactly 12 digits');
          return;
        }
        const res = await abdmApi.initiateAadhaar(input);
        setTxnId(res.data.txnId);
        setMobileHint(res.data.mobileHint ?? '');
      } else {
        if (!/^[6-9]\d{9}$/.test(input)) {
          setError('Mobile number must be 10 digits starting with 6-9');
          return;
        }
        const res = await abdmApi.initiateMobile(input);
        setTxnId(res.data.txnId);
      }
      setStage('OTP_SENT');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to send OTP. Check ABDM connectivity.');
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: verify OTP ──────────────────────────────────────────

  async function handleVerifyOtp() {
    clearError();
    if (!/^\d{6}$/.test(otp)) {
      setError('OTP must be exactly 6 digits');
      return;
    }
    setLoading(true);
    try {
      if (method === 'aadhaar') {
        await abdmApi.verifyAadhaarOtp(txnId, otp);
      } else {
        await abdmApi.verifyMobileOtp(txnId, otp);
      }

      const createRes = await abdmApi.createAbha(txnId, patientId);
      const p: AbhaProfile = {
        abhaNumber:  createRes.data.abhaNumber,
        abhaAddress: createRes.data.abhaAddress,
        name:        createRes.data.name,
        linked:      createRes.data.linked,
      };
      setProfile(p);
      setStage('PROFILE');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Step 3: confirm and close ───────────────────────────────────

  function handleConfirm() {
    if (profile) {
      onVerified(profile);
    }
    onClose();
  }

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-navy"/>
            <h2 className="font-semibold text-navy text-sm">ABDM ABHA Verification</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition"
            aria-label="Close"
          >
            <X size={16}/>
          </button>
        </div>

        <div className="p-5 space-y-4">

          {/* ── Stage: METHOD ─────────────────────────────────── */}
          {stage === 'METHOD' && (
            <>
              <p className="text-xs text-gray-500">
                {defaultMethod
                  ? `Verify ABHA via ${defaultMethod === 'aadhaar' ? 'Aadhaar OTP' : 'Mobile OTP'}.`
                  : 'Select a verification method to create or link an ABHA (Ayushman Bharat Health Account).'}
              </p>

              {!defaultMethod && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setMethod('aadhaar'); clearError(); }}
                  className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-xl border-2 text-xs font-medium transition ${
                    method === 'aadhaar'
                      ? 'border-navy bg-navy/5 text-navy'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <CreditCard size={22}/>
                  Aadhaar OTP
                </button>
                <button
                  type="button"
                  onClick={() => { setMethod('mobile'); clearError(); }}
                  className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-xl border-2 text-xs font-medium transition ${
                    method === 'mobile'
                      ? 'border-navy bg-navy/5 text-navy'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <Phone size={22}/>
                  Mobile OTP
                </button>
              </div>
              )}


              <div>
                <label className="text-xs font-medium text-gray-500">
                  {method === 'aadhaar' ? 'Aadhaar Number (12 digits)' : 'Registered Mobile Number'}
                </label>
                <input
                  type={method === 'aadhaar' ? 'text' : 'tel'}
                  inputMode="numeric"
                  maxLength={method === 'aadhaar' ? 12 : 10}
                  value={input}
                  onChange={e => { setInput(e.target.value.replace(/\D/g, '')); clearError(); }}
                  placeholder={method === 'aadhaar' ? '12-digit Aadhaar number' : '10-digit mobile number'}
                  className={`${inputCls} mt-1`}
                />
                {method === 'aadhaar' && (
                  <p className="text-xs text-gray-400 mt-1">
                    Aadhaar number is used only to send OTP via UIDAI. It is not stored.
                  </p>
                )}
              </div>

              {error && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg">{error}</p>}

              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loading || input.length < 10}
                className={btnPrimary}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </>
          )}

          {/* ── Stage: OTP_SENT ───────────────────────────────── */}
          {stage === 'OTP_SENT' && (
            <>
              <p className="text-xs text-gray-500">
                {method === 'aadhaar' && mobileHint
                  ? `OTP sent to mobile ending in ${mobileHint} linked with Aadhaar.`
                  : 'OTP sent to the registered mobile number.'}
              </p>

              <div>
                <label className="text-xs font-medium text-gray-500">Enter 6-digit OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); clearError(); }}
                  placeholder="Enter OTP"
                  className={`${inputCls} mt-1 tracking-widest text-center text-lg font-mono`}
                  autoFocus
                />
              </div>

              {error && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg">{error}</p>}

              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={loading || otp.length !== 6}
                className={btnPrimary}
              >
                {loading ? 'Verifying...' : 'Verify and Create ABHA'}
              </button>

              <button
                type="button"
                onClick={() => { setStage('METHOD'); setOtp(''); clearError(); }}
                className={btnSecondary}
              >
                Back
              </button>
            </>
          )}

          {/* ── Stage: PROFILE ────────────────────────────────── */}
          {stage === 'PROFILE' && profile && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                  <ShieldCheck size={16}/>
                  ABHA Created Successfully
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div><span className="font-medium">Name:</span> {profile.name}</div>
                  <div><span className="font-medium">ABHA Address:</span> {profile.abhaAddress}</div>
                  <div>
                    <span className="font-medium">ABHA Number:</span>{' '}
                    {profile.abhaNumber.replace(/(\d{2})(\d{4})(\d{4})(\d{4})/, '$1-$2-$3-$4')}
                  </div>
                  {profile.linked && (
                    <div className="text-green-600 font-medium">Linked to patient record.</div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                {patientId
                  ? 'The ABHA details have been saved to this patient record.'
                  : 'The ABHA details will be pre-filled in the registration form.'}
              </p>

              <button
                type="button"
                onClick={handleConfirm}
                className={btnPrimary}
              >
                {patientId ? 'Done' : 'Use These Details'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
